import { lazy, Suspense, useEffect, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom-v5-compat";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Slider,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Close from "@mui/icons-material/Close";
import { useDuress } from "./DuressProvider";
import {
  CLAIM_BANNER_MS,
  claimTime,
  describeError,
  elapsed,
  handlingTime,
  showIncidentBanner,
} from "./duressClient";

const DuressLocationMap = lazy(() => import("./DuressLocationMap"));

const timeText = (time) => (time ? new Date(time).toLocaleString() : "Unknown");
const stateLabel = (incident) =>
  incident.is_test === true
    ? incident.status === "resolved"
      ? "Test acknowledged"
      : "Test · Acknowledge required"
    : incident.status === "resolved"
    ? "Resolved"
    : incident.status === "acknowledged"
    ? "Being handled"
    : "Needs attention";
const eventLabels = {
  activated: "Duress activated",
  claimed: "Operator took control",
  taken_over: "Responsibility transferred",
  repeat_activation: "Button pressed again",
  released: "Button released",
  late_activation: "Delayed activation received",
  late_release: "Delayed release received",
  resolved: "Incident resolved",
  notifications_queued: "Notifications queued",
  test_acknowledged: "Test alert acknowledged",
  promoted_to_duress: "Duress detected outside home base",
};

function HandlingTimer({ incident, countdown = false }) {
  const [, tick] = useState(0);
  const startedAt = claimTime(incident);
  const endedAt = incident.status === "resolved" ? incident.resolved_at : null;
  useEffect(() => {
    if (startedAt === null || endedAt) return undefined;
    const timer = window.setInterval(() => tick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [startedAt, endedAt]);
  if (startedAt === null) return <span>Handling time unavailable</span>;
  const remaining = Math.max(
    0,
    Math.ceil((startedAt + CLAIM_BANNER_MS - Date.now()) / 1000)
  );
  const label = countdown
    ? `Moves to Duress in ${remaining}s`
    : `${endedAt ? "Handled for" : "Handling for"} ${handlingTime(
        startedAt,
        endedAt || Date.now()
      )}`;
  return (
    <Box
      component="span"
      role="timer"
      aria-live="off"
      sx={{
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

function SoundControls() {
  const { audio, pauseMotion, setPauseMotion } = useDuress();
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">{audio.label}</Typography>
      <Button variant="outlined" onClick={audio.enable}>
        {audio.ready ? "Test alert sound" : "Enable / test alert sound"}
      </Button>
      <Typography id="duress-volume-label" variant="caption">
        Alert volume
      </Typography>
      <Slider
        aria-labelledby="duress-volume-label"
        value={audio.volume}
        onChange={(_, value) => audio.setVolume(value)}
        min={5}
        max={100}
        valueLabelDisplay="auto"
      />
      <FormControlLabel
        control={
          <Switch
            checked={pauseMotion}
            onChange={(_, checked) => setPauseMotion(checked)}
          />
        }
        label="Pause screen-edge animation"
      />
    </Stack>
  );
}
export function DuressMenu() {
  const {
    connected,
    error,
    loaded,
    setShowHistory,
    setSelectedId,
    incidents,
    audio,
    startTest,
    testing,
    localTestMode,
  } = useDuress();
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Tooltip
        title={
          localTestMode
            ? "Alert test"
            : error ||
              (!loaded
                ? "Checking duress monitoring"
                : connected
                ? "Duress monitoring connected"
                : "Duress monitoring using backup polling")
        }
      >
        <Button
          color={incidents.length ? "warning" : "inherit"}
          size="small"
          startIcon={<ShieldOutlined />}
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label="Duress monitoring and sound"
          aria-expanded={Boolean(anchor)}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Duress
          </Box>
          {incidents.length > 0 && (
            <Box component="span" sx={{ ml: 0.5 }}>
              ({incidents.length})
            </Box>
          )}
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 7,
              height: 7,
              ml: 0.5,
              borderRadius: "50%",
              bgcolor: error || !audio.ready ? "#ffc66d" : "#85e6b8",
            }}
          />
        </Button>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Stack spacing={2} sx={{ p: 2.5, width: 320, maxWidth: "90vw" }}>
          <Typography variant="h6">Duress monitoring</Typography>
          {localTestMode ? (
            <Typography variant="body2" color="text.secondary">
              Local alert test
            </Typography>
          ) : error ? (
            <Alert severity="warning">Monitoring unavailable. {error}</Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {!loaded
                ? "Connecting…"
                : connected
                ? "Live updates connected"
                : "Checking alerts every 5 seconds"}
            </Typography>
          )}
          {incidents.length > 0 && (
            <List
              dense
              disablePadding
              aria-label="Open duress incidents"
              sx={{ maxHeight: "35vh", overflowY: "auto" }}
            >
              {incidents.map((incident) => (
                <ListItem key={incident.id} disableGutters>
                  <Button
                    fullWidth
                    color={
                      incident.is_test === true
                        ? "info"
                        : incident.status === "unacknowledged"
                        ? "error"
                        : "inherit"
                    }
                    aria-label={`View duress incident for ${
                      incident.current_vehicle_name || incident.vehicle_name
                    }`}
                    sx={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      textTransform: "none",
                    }}
                    onClick={() => {
                      setShowHistory(false);
                      setSelectedId(incident.id);
                      setAnchor(null);
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2">
                        {incident.current_vehicle_name || incident.vehicle_name}
                      </Typography>
                      <Typography variant="caption">
                        {incident.is_test === true
                          ? "Test alert · Acknowledge required"
                          : incident.status === "acknowledged"
                          ? `Being handled by ${
                              incident.owner_name || "an operator"
                            }`
                          : "Needs attention"}
                      </Typography>
                    </Box>
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
          <SoundControls />
          <Divider />
          {!localTestMode && loaded && (
            <Button
              onClick={() => {
                setSelectedId(null);
                setShowHistory(true);
                setAnchor(null);
              }}
            >
              Incident history
            </Button>
          )}
          {startTest && (
            <Button
              disabled={!testing && incidents.length > 0}
              variant="outlined"
              onClick={() => {
                startTest();
                setAnchor(null);
              }}
            >
              {testing ? "Restart test" : "Test alert"}
            </Button>
          )}
        </Stack>
      </Popover>
    </>
  );
}
export function DuressBanner() {
  const {
    incidents,
    alarmKey,
    pauseMotion,
    setSelectedId,
    audio,
    error,
    act,
    testing,
    endTest,
    releaseTest,
    pressTest,
  } = useDuress();
  const [expanded, setExpanded] = useState(false),
    [busy, setBusy] = useState(null),
    [actionError, setActionError] = useState("");
  const [, tick] = useState(0);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  useEffect(() => {
    if (!incidents.length) return undefined;
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [incidents.length]);
  const visibleIncidents = incidents.filter((incident) =>
    showIncidentBanner(incident)
  );
  const testsOnly =
    visibleIncidents.length > 0 &&
    visibleIncidents.every((incident) => incident.is_test === true);
  if (!visibleIncidents.length && !testing) return null;
  const claim = async (incident) => {
    setBusy(incident.id);
    setActionError("");
    try {
      await act(incident, incident.is_test === true ? "acknowledge" : "claim");
    } catch (failure) {
      setActionError(describeError(failure));
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      {alarmKey && (
        <Box
          aria-hidden
          data-testid="duress-edge"
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1600,
            pointerEvents: "none",
            border: "5px solid #dc2626",
            animation:
              pauseMotion || reduceMotion
                ? "none"
                : "duressPulse 2s ease-in-out infinite",
            "@keyframes duressPulse": {
              "0%,100%": { opacity: 0.3 },
              "50%": { opacity: 0.9 },
            },
          }}
        />
      )}
      <Box
        component="section"
        aria-label="Active duress incidents"
        sx={{
          flexShrink: 0,
          maxHeight: "50vh",
          overflowY: "auto",
          background: testsOnly ? "#eff6ff" : alarmKey ? "#fff1f2" : "#fff8f4",
          color: testsOnly ? "#17365d" : "#491b24",
          borderBottom: `1px solid ${testsOnly ? "#bfdbfe" : "#f3c5c9"}`,
          borderTop: `3px solid ${
            testsOnly ? "#2563eb" : alarmKey ? "#dc2626" : "#b97441"
          }`,
          px: { xs: 1.5, md: 3 },
          py: 1.5,
        }}
      >
        {testing && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ mb: 1 }}
          >
            <Chip label="TEST ALERT" size="small" color="info" />
            <Typography variant="caption">
              Only visible in this browser
            </Typography>
            <Button
              size="small"
              onClick={
                incidents[0]?.input_value === 1 ? releaseTest : pressTest
              }
            >
              {incidents[0]?.input_value === 1
                ? "Release test button"
                : incidents.length
                ? "Press again"
                : "Trigger another alert"}
            </Button>
            <Button size="small" onClick={endTest}>
              End test
            </Button>
          </Stack>
        )}
        <Box
          role="alert"
          aria-atomic="true"
          sx={{
            position: "absolute",
            // Numeric 1 means 100% in MUI sizing and creates page overflow.
            width: "1px",
            height: "1px",
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
          }}
        >
          {alarmKey
            ? `${
                incidents.filter(
                  (i) => i.status === "unacknowledged" && i.is_test !== true
                ).length
              } duress incidents need attention. ${incidents
                .filter(
                  (i) => i.status === "unacknowledged" && i.is_test !== true
                )
                .map((i) => i.current_vehicle_name || i.vehicle_name)
                .join(", ")}`
            : testsOnly
            ? "Home-base test alerts require acknowledgement."
            : ""}
        </Box>
        {(expanded ? visibleIncidents : visibleIncidents.slice(0, 1)).map(
          (incident) => (
            <Stack
              key={incident.id}
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1.5}
              sx={{ py: 0.5 }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <WarningAmberRounded
                  sx={{
                    color: incident.is_test === true ? "#2563eb" : "#c12135",
                    fontSize: 30,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: incident.is_test === true ? "#1d4ed8" : "#a5142c",
                      fontWeight: 800,
                      lineHeight: 1.3,
                      letterSpacing: 1.5,
                    }}
                  >
                    {incident.is_test === true ? "TEST ALERT" : "DURESS"}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
                  >
                    {incident.current_vehicle_name || incident.vehicle_name}
                  </Typography>
                  <Typography variant="body2">
                    {incident.is_test === true
                      ? `Home base${
                          incident.test_geofences?.length
                            ? ` · ${incident.test_geofences
                                .map((zone) => zone.name)
                                .join(", ")}`
                            : ""
                        }`
                      : incident.status === "acknowledged"
                      ? `Being handled by ${
                          incident.owner_name || "an operator"
                        }`
                      : "Operator response required"}{" "}
                    · Alerted {elapsed(incident.detected_at)} ago ·{" "}
                    {incident.last_signal_location ||
                    incident.activation_location
                      ? "Location available"
                      : "Location unavailable"}
                  </Typography>
                  {incident.status === "acknowledged" && (
                    <Typography variant="body2">
                      <HandlingTimer incident={incident} countdown />
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {incident.status === "unacknowledged" && (
                  <Button
                    variant="contained"
                    color={incident.is_test === true ? "info" : "error"}
                    disabled={busy === incident.id}
                    onClick={() => claim(incident)}
                  >
                    {incident.is_test === true
                      ? busy === incident.id
                        ? "Acknowledging…"
                        : "Acknowledge"
                      : busy === incident.id
                      ? "Taking control…"
                      : "Take control"}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color={incident.is_test === true ? "info" : "error"}
                  onClick={() => setSelectedId(incident.id)}
                >
                  {incident.last_signal_location || incident.activation_location
                    ? "View map"
                    : "View details"}
                </Button>
              </Stack>
            </Stack>
          )
        )}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ mt: 0.5 }}
        >
          {visibleIncidents.length > 1 && (
            <Button
              size="small"
              color="error"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
            >
              {expanded
                ? "Show less"
                : `View all ${visibleIncidents.length} incidents`}
            </Button>
          )}
          {alarmKey && !audio.ready && (
            <Button color="error" size="small" onClick={audio.enable}>
              {audio.blocked
                ? "Sound blocked — enable sound"
                : "Enable alert sound"}
            </Button>
          )}
          {alarmKey && audio.ready && (
            <Typography variant="caption">{audio.label}</Typography>
          )}
        </Stack>
        {(error || actionError) && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {actionError ||
              `Connection problem. Last known alerts remain visible. ${error}`}
          </Alert>
        )}
      </Box>
    </>
  );
}
function IncidentHistory() {
  const { api, setSelectedId, setShowHistory, testing, testHistory } =
    useDuress();
  const [rows, setRows] = useState([]),
    [cursor, setCursor] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const load = async (before) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.get(`${api}/v2/duress/incidents`, {
        params: { history: true, before },
        timeout: 10000,
      });
      setRows((current) =>
        before ? [...current, ...data.incidents] : data.incidents
      );
      setCursor(data.next_cursor);
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (testing) setRows(testHistory || []);
    else load();
  }, [api, testing, testHistory]);
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Incident history</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!rows.length && !busy && (
        <Typography color="text.secondary">No incidents to display.</Typography>
      )}
      <List disablePadding>
        {rows.map((entry) => (
          <ListItem key={entry.id} disableGutters divider>
            <ListItemText
              primary={entry.current_vehicle_name || entry.vehicle_name}
              secondary={`${stateLabel(entry)} · ${timeText(
                entry.activated_at
              )}`}
            />
            <Button
              size="small"
              onClick={() => {
                setSelectedId(entry.id);
                setShowHistory(false);
              }}
            >
              Open
            </Button>
          </ListItem>
        ))}
      </List>
      {busy && <CircularProgress size={24} />}
      {cursor && (
        <Button disabled={busy} onClick={() => load(cursor)}>
          Load older incidents
        </Button>
      )}
    </Stack>
  );
}
function IncidentDetail({ id }) {
  const { api, user, revision, act, testing, getTestIncident } = useDuress();
  const [incident, setIncident] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [note, setNote] = useState("");
  const [confirmTakeover, setConfirmTakeover] = useState(false);
  useEffect(() => {
    let current = true;
    if (testing) {
      setIncident(getTestIncident(id));
      return undefined;
    }
    axios
      .get(`${api}/v2/duress/incidents/${id}`, { timeout: 10000 })
      .then(({ data }) => {
        if (current) {
          setIncident(data);
          setError("");
        }
      })
      .catch((failure) => {
        if (current) {
          setError(describeError(failure));
          if ([403, 404].includes(failure?.response?.status)) setIncident(null);
        }
      });
    return () => {
      current = false;
    };
  }, [api, id, revision, testing, getTestIncident]);
  const perform = async (action) => {
    setBusy(true);
    setError("");
    try {
      setIncident(await act(incident, action, { note }));
      setConfirmTakeover(false);
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  };
  const isOwner = incident?.owner_id === user.username_id;
  const canResolve =
    typeof incident?.release_required === "boolean"
      ? !incident.release_required
      : incident?.input_value === 0 &&
        new Date(incident.input_observed_at) >=
          new Date(incident.last_activation_at);
  const doublePressOnly =
    incident?.input_activation_at === null &&
    incident?.activation_source === "double_button_press";
  return (
    <Stack spacing={2.5}>
      {error && <Alert severity="error">{error}</Alert>}
      {!incident ? (
        !error && <CircularProgress size={24} />
      ) : (
        <>
          <Box>
            <Typography
              variant="overline"
              color={incident.is_test === true ? "info.main" : "error"}
            >
              {incident.is_test === true
                ? "TEST ALERT"
                : `DURESS${testing ? " · TEST ALERT" : ""}`}
            </Typography>
            <Typography variant="h5">
              {incident.current_vehicle_name || incident.vehicle_name}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Activated {timeText(incident.activated_at)}
              {incident.recovered ? " · recovered telemetry" : ""}
            </Typography>
          </Box>
          {incident.is_test === true && (
            <Alert severity="info">
              Triggered inside home base
              {incident.test_geofences?.length
                ? `: ${incident.test_geofences
                    .map((zone) => zone.name)
                    .join(", ")}`
                : ""}
              . Acknowledge to close this test. No resolution reason is
              required.
            </Alert>
          )}
          <Stack direction="row" spacing={1}>
            <Chip
              label={stateLabel(incident)}
              color={
                incident.is_test === true
                  ? "info"
                  : incident.status === "unacknowledged"
                  ? "error"
                  : "default"
              }
            />
            <Chip
              variant="outlined"
              label={
                doublePressOnly
                  ? "Double press received"
                  : incident.input_value === 1
                  ? "Button active"
                  : incident.input_value === 0
                  ? "Button released"
                  : "Input unknown"
              }
            />
          </Stack>
          {incident.owner_id && (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                {incident.is_test === true
                  ? "Acknowledged by"
                  : incident.status === "resolved"
                  ? "Handled by"
                  : "Being handled by"}{" "}
                <strong>
                  {incident.owner_name || `operator ${incident.owner_id}`}
                </strong>
              </Typography>
              {incident.is_test !== true && (
                <Typography variant="body2">
                  <HandlingTimer incident={incident} />
                </Typography>
              )}
            </Stack>
          )}
          {incident.status === "unacknowledged" && (
            <Button
              color={incident.is_test === true ? "info" : "error"}
              variant="contained"
              disabled={busy}
              onClick={() =>
                perform(incident.is_test === true ? "acknowledge" : "claim")
              }
            >
              {incident.is_test === true ? "Acknowledge" : "Take control"}
            </Button>
          )}
          <Suspense
            fallback={<Typography role="status">Loading map…</Typography>}
          >
            <DuressLocationMap incident={incident} />
          </Suspense>
          {incident.driver && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2">{incident.driver.name}</Typography>
              {incident.driver.mobile && (
                <Button
                  component="a"
                  href={`tel:${incident.driver.mobile}`}
                  size="small"
                >
                  Call driver
                </Button>
              )}
            </Stack>
          )}
          {!testing && (
            <Button
              component={RouterLink}
              to={`/dashboard/assets/device/${encodeURIComponent(
                incident.imei
              )}`}
              size="small"
            >
              Open vehicle dashboard
            </Button>
          )}
          {incident.is_test === true ? (
            incident.status === "resolved" && (
              <Alert severity="success">
                Acknowledged{" "}
                {timeText(incident.acknowledged_at || incident.resolved_at)}.
              </Alert>
            )
          ) : incident.status !== "resolved" ? (
            <Stack spacing={1.5}>
              {incident.owner_id &&
                !isOwner &&
                (!confirmTakeover ? (
                  <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={() => setConfirmTakeover(true)}
                  >
                    Take over responsibility
                  </Button>
                ) : (
                  <Alert
                    severity="warning"
                    action={
                      <Button
                        disabled={busy}
                        onClick={() => perform("takeover")}
                      >
                        Confirm takeover
                      </Button>
                    }
                  >
                    You will replace the current operator.
                  </Alert>
                ))}
              {isOwner && (
                <>
                  <TextField
                    label="Resolution note"
                    multiline
                    minRows={2}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    inputProps={{
                      maxLength: 2000,
                      "aria-label": "Resolution note",
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {canResolve
                      ? doublePressOnly
                        ? "Double press received. Add a note to close the incident."
                        : "Button release confirmed. Add a note to close the incident."
                      : "Resolution requires a confirmed release after the latest button activation."}
                  </Typography>
                  <Button
                    variant="contained"
                    disabled={busy || !canResolve || !note.trim()}
                    onClick={() => perform("resolve")}
                  >
                    Resolve incident
                  </Button>
                </>
              )}
            </Stack>
          ) : (
            <Alert severity="success">
              Resolved {timeText(incident.resolved_at)}.{" "}
              {incident.resolution_note}
            </Alert>
          )}
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              borderTop: 1,
              borderColor: "divider",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body2">
                {incident.deliveries?.length
                  ? "Timeline & notifications"
                  : "Timeline"}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <Stack spacing={2}>
                {Boolean(incident.deliveries?.length) && (
                  <Box>
                    <Typography variant="subtitle1">SMS and email</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Accepted means the provider accepted the message; it does
                      not confirm it was read.
                    </Typography>
                    <List dense disablePadding>
                      {(incident.deliveries || []).map((delivery, index) => (
                        <ListItem key={delivery.id || index} disableGutters>
                          <ListItemText
                            primary={`${
                              delivery.recipient_name || "Recipient"
                            } · ${delivery.channel.toUpperCase()}`}
                            secondary={delivery.status}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle1">Timeline</Typography>
                  <List dense disablePadding>
                    {(incident.events || []).map((event, index) => (
                      <ListItem
                        key={event.id || index}
                        disableGutters
                        alignItems="flex-start"
                      >
                        <ListItemText
                          primary={`${
                            event.type === "activated" &&
                            event.detail?.is_test === true
                              ? "Test alert activated"
                              : eventLabels[event.type] || event.type
                          }${
                            event.detail?.source === "double_button_press"
                              ? " · Double press"
                              : ""
                          }${event.actor_name ? ` · ${event.actor_name}` : ""}`}
                          secondary={`${timeText(event.occurred_at)}${
                            event.detail?.note ? ` — ${event.detail.note}` : ""
                          }`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Stack>
  );
}
export function DuressDrawer() {
  const { selectedId, setSelectedId, showHistory, setShowHistory } =
    useDuress();
  const close = () => {
    setSelectedId(null);
    setShowHistory(false);
  };
  return (
    <Drawer
      anchor="right"
      open={Boolean(selectedId || showHistory)}
      onClose={close}
      sx={{ zIndex: 1500 }}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 460 }, maxWidth: "100vw" },
        role: "dialog",
        "aria-label": "Duress response",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 2 }}
      >
        <Typography variant="h6">Duress response</Typography>
        <IconButton aria-label="Close duress response" onClick={close}>
          <Close />
        </IconButton>
      </Stack>
      <Divider />
      <Box sx={{ p: 3, overflowY: "auto" }}>
        {showHistory ? (
          <IncidentHistory />
        ) : selectedId ? (
          <IncidentDetail key={selectedId} id={selectedId} />
        ) : null}
      </Box>
    </Drawer>
  );
}
